import React, { useState, useEffect } from 'react';
import { db } from '../services/db';
import { ClientEntity, GlobalSettings, Payment } from '../types';
import { Loader2, DollarSign, Filter, Search, CheckCircle, XCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';

interface AdminFinanceBoardProps {
  clients: ClientEntity[];
  onClientUpdate: () => void;
}

export const AdminFinanceBoard: React.FC<AdminFinanceBoardProps> = ({ clients, onClientUpdate }) => {
  const [globalSettings, setGlobalSettings] = useState<GlobalSettings | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [priceInput, setPriceInput] = useState('');
  const [pixInput, setPixInput] = useState('');

  // Editing client price
  const [editingClientId, setEditingClientId] = useState<string | null>(null);
  const [clientPriceInput, setClientPriceInput] = useState('');
  const [savingClientCode, setSavingClientCode] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [settings, allPayments] = await Promise.all([
        db.getGlobalSettings(),
        db.getAllPayments()
      ]);
      setGlobalSettings(settings);
      setPriceInput(settings.defaultSitePrice.toString());
      setPixInput(settings.pixCnpj || '');
      setPayments(allPayments);
    } catch (err) {
      toast.error('Erro ao carregar dados financeiros.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGlobal = async () => {
    setSavingSettings(true);
    try {
      const parsedPrice = parseFloat(priceInput.replace(',', '.'));
      if (isNaN(parsedPrice)) {
        toast.error('Preço global inválido.');
        return;
      }
      await db.updateGlobalSettings({
        defaultSitePrice: parsedPrice,
        pixCnpj: pixInput
      });
      toast.success('Configurações globais salvas!');
      loadData();
    } catch (err) {
      toast.error('Erro ao salvar as configurações.');
    } finally {
      setSavingSettings(false);
    }
  };

  const saveClientPrice = async (clientId: string) => {
    setSavingClientCode(clientId);
    try {
      let val: number | null = parseFloat(clientPriceInput.replace(',', '.'));
      if (isNaN(val)) val = null;

      await db.updateClientPrice(clientId, val);
      toast.success('Preço do cliente atualizado!');
      setEditingClientId(null);
      onClientUpdate();
    } catch (err) {
      toast.error('Erro ao atualizar preço do cliente.');
    } finally {
      setSavingClientCode(null);
    }
  };

  const handleApprovePix = async (paymentId: string) => {
    try {
      const paymentToApprove = payments.find(p => p.id === paymentId);
      if (!paymentToApprove) return;
      
      await db.updatePaymentStatus(paymentId, 'paid');
      
      // Update client status to active
      await db.updateClientStatus(paymentToApprove.clientId, 'active');

      toast.success('Pagamento aprovado e site liberado!');
      loadData();
    } catch (err) {
      toast.error('Erro ao aprovar o PIX.');
    }
  };

  const handleRejectPix = async (paymentId: string) => {
    try {
      await db.updatePaymentStatus(paymentId, 'failed');
      toast.success('Pagamento rejeitado.');
      loadData();
    } catch (err) {
      toast.error('Erro ao rejeitar o PIX.');
    }
  };

  const handleGenerateCheckout = async (clientId: string) => {
    try {
      const loadingToast = toast.loading('Gerando link do Stripe...');
      
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Erro ao gerar o link');
      }

      await navigator.clipboard.writeText(data.url);
      toast.success('Link do checkout copiado para a área de transferência!', { id: loadingToast });
    } catch (error: any) {
      console.error(error);
      toast.error(error.message || 'Falha ao conectar com o servidor.', { id: loadingToast });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="animate-spin text-indigo-600" size={40} />
      </div>
    );
  }

  const pendingPayments = payments.filter(p => p.paymentMethod === 'pix_manual' && p.status === 'verifying');
  const pastPayments = payments.filter(p => p.status !== 'verifying');

  return (
    <div className="space-y-8 animate-in fade-in duration-300">
      
      {/* Global Config Section */}
      <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <DollarSign size={20} />
          Configurações Globais
        </h2>
        
        <div className="flex flex-col sm:flex-row gap-6">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Preço Global dos Sites (R$)</label>
            <input 
              type="text" 
              value={priceInput}
              onChange={(e) => setPriceInput(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black outline-none"
              placeholder="Ex: 99.90"
            />
            <p className="text-xs text-gray-500 mt-1">Este preço será aplicado a todos os clientes que não possuírem preço customizado.</p>
          </div>
          
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Chave PIX (CNPJ)</label>
            <input 
              type="text" 
              value={pixInput}
              onChange={(e) => setPixInput(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black outline-none"
              placeholder="00.000.000/0001-00"
            />
            <p className="text-xs text-gray-500 mt-1">Sua chave PIX CNPJ informada para o cliente depositar sem taxa.</p>
          </div>
          
          <div className="flex items-end">
            <button 
              onClick={handleSaveGlobal} 
              disabled={savingSettings}
              className="bg-black hover:bg-gray-900 text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 h-[42px] min-w-[120px]"
            >
              {savingSettings ? <Loader2 className="animate-spin" size={18} /> : 'Salvar'}
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        {/* Pending Approvals */}
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex flex-col h-[500px]">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Clock size={20} className="text-amber-500" />
            PIX Pendentes de Aprovação ({pendingPayments.length})
          </h2>
          
          <div className="flex-1 overflow-y-auto pr-2 space-y-4">
            {pendingPayments.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <CheckCircle size={32} className="mx-auto mb-2 text-gray-300" />
                <p>Nenhuma aprovação pendente no momento.</p>
              </div>
            ) : (
              pendingPayments.map(payment => {
                const client = clients.find(c => c.id === payment.clientId);
                return (
                  <div key={payment.id} className="border border-amber-200 bg-amber-50 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-semibold text-gray-900">{client ? client.data.name : 'Cliente desconhecido'}</h3>
                        <p className="text-sm text-gray-600">
                          Valor: <span className="font-bold text-green-700">R$ {payment.amount.toFixed(2)}</span>
                        </p>
                        <p className="text-xs text-gray-500 mt-1">Data: {new Date(payment.createdAt).toLocaleString('pt-BR')}</p>
                      </div>
                      {payment.receiptUrl ? (
                         <a href={payment.receiptUrl} target="_blank" rel="noreferrer" className="text-xs bg-indigo-100 hover:bg-indigo-200 text-indigo-700 px-3 py-1.5 rounded-md font-medium transition-colors">
                           Ver Comprovante
                         </a>
                      ) : (
                         <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded">Sem Comprovante anexo</span>
                      )}
                    </div>
                    
                    <div className="flex gap-2">
                      <button 
                        onClick={() => handleApprovePix(payment.id)}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 rounded-md font-medium text-sm flex items-center justify-center gap-1 transition-colors"
                      >
                        <CheckCircle size={16} /> Aprovar Liberação
                      </button>
                      <button 
                        onClick={() => handleRejectPix(payment.id)}
                        className="flex-1 bg-red-100 hover:bg-red-200 text-red-700 py-2 rounded-md font-medium text-sm flex items-center justify-center gap-1 transition-colors"
                      >
                        <XCircle size={16} /> Rejeitar
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Client Custom Pricing */}
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex flex-col h-[500px]">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Preços Personalizados por Cliente</h2>
          <div className="mb-4">
            <p className="text-sm text-gray-500 mb-2">Configure um preço específico para um cliente. Deixe em branco para usar o preço global (R$ {globalSettings?.defaultSitePrice.toFixed(2)}).</p>
          </div>
          
          <div className="flex-1 overflow-y-auto pr-2 border border-gray-100 rounded-lg">
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                <tr>
                  <th className="px-4 py-3 font-medium text-gray-600">Cliente</th>
                  <th className="px-4 py-3 font-medium text-gray-600 text-right w-32">Preço (R$)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {clients.map(client => (
                  <tr key={client.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900 truncate max-w-[180px]">{client.data.name}</div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {editingClientId === client.id ? (
                        <div className="flex items-center justify-end gap-1">
                          <input 
                            type="text" 
                            autoFocus
                            value={clientPriceInput}
                            onChange={(e) => setClientPriceInput(e.target.value)}
                            className="w-16 px-2 py-1 text-right border border-gray-300 rounded text-sm outline-none focus:border-black"
                            placeholder="Global"
                          />
                          <button 
                            disabled={savingClientCode === client.id}
                            onClick={() => saveClientPrice(client.id)}
                            className="text-green-600 hover:text-green-800 p-1"
                          >
                            <CheckCircle size={16} />
                          </button>
                          <button 
                            onClick={() => setEditingClientId(null)}
                            className="text-gray-400 hover:text-gray-600 p-1"
                          >
                            <XCircle size={16} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-end gap-3 group">
                          <button 
                            onClick={() => handleGenerateCheckout(client.id)}
                            className="text-xs bg-indigo-50 hover:bg-indigo-100 text-indigo-700 px-2 py-1 rounded transition-colors opacity-0 group-hover:opacity-100 flex items-center gap-1"
                            title="Gerar Link de Pagamento Stripe"
                          >
                            <DollarSign size={12} />
                            Link Checkout
                          </button>
                          <div 
                            className="cursor-pointer hover:bg-gray-100 px-2 py-1 rounded inline-flex items-center gap-1"
                            onClick={() => {
                              setEditingClientId(client.id);
                              setClientPriceInput(client.customPrice !== null && client.customPrice !== undefined ? client.customPrice.toString() : '');
                            }}
                          >
                            <span className={client.customPrice ? 'font-bold text-indigo-700' : 'text-gray-400 italic'}>
                              {client.customPrice ? client.customPrice.toFixed(2) : 'Global'}
                            </span>
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
};
